import React from 'react';
import { Box, Card, CardContent, Chip, Grid, Typography, alpha, useTheme } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../store';
import { getFormattedContact } from '../../services/getFormattedContact';

interface ContactDetails {
  companyName: string;
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  email: string;
  phoneNumber: string;
  telephoneCode: string;
  altContactName?: string;
  altContactNo?: string;
  altContactTelephoneCode?: string;
  IdType?: string;
  IdNumber?: string;
}

function ShipperConsigneeCard({ type, data }: { type: 'shipper' | 'consignee'; data: ContactDetails }) {
  const theme = useTheme();
  const isShipper = type === 'shipper';
  const title = isShipper ? 'Shipper Information' : 'Consignee Information';
  const chipLabel = isShipper ? 'Origin' : 'Destination';
  const subtitle = isShipper ? 'Sender details & dispatch location' : 'Recipient details & delivery address';
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesObj);
  const countryName = countryHashById?.[data.country]?.name || data.country;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LocalShippingOutlinedIcon />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>{title}</Typography>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>{subtitle}</Typography>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
              Full Name
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary', wordBreak: 'break-word' }}>
              {data.name || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
              Company Name
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary', wordBreak: 'break-word' }}>
              {data.companyName || '—'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
              Mobile No.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CallOutlinedIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>
                {data.phoneNumber ? getFormattedContact(`${data.telephoneCode || ''}${data.phoneNumber}`) : '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>Email</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MailOutlineIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '14px', color: 'text.primary', wordBreak: 'break-all' }}>
                {data.email || '—'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
              Address Line 1
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary', wordBreak: 'break-word' }}>
              {data.line1 || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
              Address Line 2
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary', wordBreak: 'break-word' }}>
              {data.line2 || '—'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>City</Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{data.city || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>State</Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{data.state || '—'}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>Country</Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{countryName || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>Pincode</Typography>
            <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{data.pincode || '—'}</Typography>
          </Grid>

          {data.IdType && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
                  ID Type
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{data.IdType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
                  ID Number
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{data.IdNumber || '—'}</Typography>
              </Grid>
            </>
          )}

          {data.altContactName && (
            <Grid item xs={12} sx={{ mt: 1, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 0.5 }}>
                Alternate Contact
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>
                {data.altContactName}
                {data.altContactNo && ` (${data.altContactTelephoneCode || ''} ${data.altContactNo})`}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

function ShipperConsigneeDetails() {
  const { shipmentById } = useSelector((state: RootStateType) => state.shipment);
  const { _consigneeId, _shipperId, extraDetails } = shipmentById || {};

  const shipper = React.useMemo<ContactDetails>(
    () => ({
      companyName: _shipperId?.companyName || '',
      line1: _shipperId?.line1 || '',
      line2: _shipperId?.line2 || '',
      city: _shipperId?.city || '',
      state: _shipperId?.state || '',
      country: _shipperId?.country || '',
      pincode: _shipperId?.pincode || '',
      name: _shipperId?.name || '',
      phoneNumber: _shipperId?.phone?.number || '',
      telephoneCode: _shipperId?.phone?.telephoneCode || '',
      email: _shipperId?.email || '',
      altContactName: _shipperId?.alternatePhone?.name || '',
      altContactNo: _shipperId?.alternatePhone?.number || '',
      altContactTelephoneCode: _shipperId?.alternatePhone?.telephoneCode || '',
      IdType: extraDetails?.shipper?.IdType || '',
      IdNumber: extraDetails?.shipper?.IdNumber || '',
    }),
    [_shipperId, extraDetails],
  );

  const consignee = React.useMemo<ContactDetails>(
    () => ({
      companyName: _consigneeId?.companyName || '',
      line1: _consigneeId?.line1 || '',
      line2: _consigneeId?.line2 || '',
      city: _consigneeId?.city || '',
      state: _consigneeId?.state || '',
      country: _consigneeId?.country || '',
      pincode: _consigneeId?.pincode || '',
      name: _consigneeId?.name || '',
      phoneNumber: _consigneeId?.phone?.number || '',
      telephoneCode: _consigneeId?.phone?.telephoneCode || '',
      email: _consigneeId?.email || '',
      altContactName: _consigneeId?.alternatePhone?.name || '',
      altContactNo: _consigneeId?.alternatePhone?.number || '',
      altContactTelephoneCode: _consigneeId?.alternatePhone?.telephoneCode,
      IdType: extraDetails?.consignee?.IdType || '',
      IdNumber: extraDetails?.consignee?.IdNumber || '',
    }),
    [_consigneeId, extraDetails],
  );

  return (
    <Grid container spacing={3} sx={{ height: { lg: '100%' }, flexGrow: 1, alignItems: 'stretch' }}>
      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
        <ShipperConsigneeCard type="shipper" data={shipper} />
      </Grid>
      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
        <ShipperConsigneeCard type="consignee" data={consignee} />
      </Grid>
    </Grid>
  );
}

export default ShipperConsigneeDetails;
